import React from 'react';

import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import {
    Box,
    Button,
    Card,
    Divider,
    Grid,
    List,
    ListItem,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import isArray from 'lodash/isArray';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { KeyValue, Modal } from 'common/components';
import { PlaceholderCardContactsDashboard } from 'common/components/placeholder';
import { numberFormat } from 'common/utils/helpers';

import { ContactForm } from '../components';

const ContactFileCard = ({
    canModify,
    creditId,
    client,
    handleClickUpdate,
    handleClickCloseModalContact,
    isOpenModalContact,
    isLoadingContact,
    systemSize,
    systemGeneration,
}) => {
    const { t } = useTranslation();
    const {
        agent_name = t('No assigned advisor'),
        business_name,
        emails = [],
        first_lastname = '',
        name = '',
        origin = t('Without origin'),
        second_lastname = '',
        telephones = [],
    } = client;

    return (
        <PlaceholderCardContactsDashboard ready={isLoadingContact}>
            <Box>
                <Card>
                    <Card.Header>
                        <Typography fontWeight="bold" variant="body2">
                            {t('Client')}
                        </Typography>
                    </Card.Header>

                    <List>
                        <ListItem>
                            <Typography variant="body2">
                                {name} {first_lastname} {second_lastname}
                            </Typography>
                        </ListItem>

                        <ListItem>
                            <Typography fontWeight="bold" variant="body2">
                                {t('Business name')}: {business_name || '- - -'}
                            </Typography>
                        </ListItem>

                        <ListItem>
                            <Box>
                                {isArray(telephones) &&
                                telephones?.length > 0 ? (
                                    telephones?.map(({ telephone }, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                alignItems: 'center',
                                                display: 'flex',
                                                gap: 2,
                                            }}
                                        >
                                            <PhoneIcon fontSize="small" />
                                            <Typography variant="body2">
                                                {telephone}
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography variant="caption">
                                        {t('No phone registered')}
                                    </Typography>
                                )}

                                {isArray(emails) && emails?.length > 0 ? (
                                    <Box
                                        sx={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            gap: 2,
                                        }}
                                    >
                                        <EmailIcon fontSize="small" />
                                        <Typography variant="body2">
                                            {emails[0].email}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="caption">
                                        {t('No registered email')}
                                    </Typography>
                                )}
                            </Box>
                        </ListItem>

                        <ListItem>
                            <Box>
                                <Typography fontWeight="bold" variant="body2">
                                    {t('Assigned advisor')}:{' '}
                                    {agent_name || '- - -'}
                                </Typography>

                                <Typography fontWeight="bold" variant="body2">
                                    {t('Origin')}: {origin || '- - -'}
                                </Typography>
                            </Box>
                        </ListItem>

                        <ListItem>
                            <Grid container sx={{ width: '100%' }}>
                                <Grid size={9}>
                                    <KeyValue
                                        append="kW"
                                        title={t('System size')}
                                        value={numberFormat(systemSize, {
                                            style: 'decimal',
                                        })}
                                    />
                                </Grid>

                                <Grid size={9}>
                                    <KeyValue
                                        append="kWH"
                                        title={t('Annual generation')}
                                        value={numberFormat(systemGeneration, {
                                            style: 'decimal',
                                        })}
                                    />
                                </Grid>
                            </Grid>
                        </ListItem>
                    </List>

                    <Divider />

                    <Card.Body hidden={!canModify} sx={{ textAlign: 'center' }}>
                        <Button
                            color="secondary"
                            onClick={() => handleClickUpdate(client)}
                            variant="text"
                        >
                            {t('Edit')}
                        </Button>
                    </Card.Body>
                </Card>
            </Box>

            <Modal
                onHide={handleClickCloseModalContact}
                show={isOpenModalContact}
                size="lg"
                title={t('Edit')}
            >
                <ContactForm
                    creditId={creditId}
                    contactId={client.id}
                    handleClickCloseForm={handleClickCloseModalContact}
                />
            </Modal>
        </PlaceholderCardContactsDashboard>
    );
};

ContactFileCard.propTypes = {
    canModify: PropTypes.bool,
    creditId: PropTypes.string,
    client: PropTypes.object,
    handleClickUpdate: PropTypes.func,
    handleClickCloseModalContact: PropTypes.func,
    isOpenModalContact: PropTypes.bool,
    isLoadingContact: PropTypes.bool,
    systemSize: PropTypes.number,
    systemGeneration: PropTypes.number,
};

export default ContactFileCard;
